import axios, { type AxiosInstance } from 'axios';
import type { TempoUserResponse } from '@/common/api/types/userManagement';
import { fetchAccessToken } from '@/common/utils/fetchAccessToken';
import { getEnv } from '@/common/utils/getEnv';
import { fetchUsersByIds } from './fetchUsersByIds';
import { useAccountSettingsApi } from './useAccountSettingsApi';
import { useActivityLogsApi } from './useActivityLogsApi';
import { useBulkImportApi } from './useBulkImportApi';
import { useCompletionEventsApi } from './useCompletionEventsApi';
import { useCompletionNotificationsApi } from './useCompletionNotificationsApi';
import { useCustomersApi } from './useCustomersApi';
import { useCustomFieldsApi } from './useCustomFieldsApi';
import { useDemandChecklistApi } from './useDemandChecklistApi';
import { useDemandReviewersApi } from './useDemandReviewersApi';
import { useDemandStatusesApi } from './useDemandStatusesApi';
import { useDemandsApi } from './useDemandsApi';
import { useDemandTypesApi } from './useDemandTypesApi';
import { useDemandViewsApi } from './useDemandViewsApi';
import { useFeedbackApi } from './useFeedbackApi';
import { useFeedbackFormsApi } from './useFeedbackFormsApi';
import { useIdeasApi } from './useIdeasApi';
import { useIdeaViewsApi } from './useIdeaViewsApi';
import { useVotesApi } from './useIdeaVotesApi';
import { useNotificationSubscriptions } from './useNotificationSubscriptions';
import { usePortalsApi } from './usePortalsApi';
import { usePriorityFrameworkApi } from './usePriorityFrameworkApi';
import { useProductAreasApi } from './useProductAreasApi';
import { useRanksApi } from './useRanksApi';
import { useS3Api } from './useS3Api';
import { useUserInteractionsApi } from './useUserInteractionsApi';

interface IData {
  id: number;
  displayName?: string | null | undefined;
  creatorName?: string | null | undefined;
  email?: string | null | undefined;
  emailAddress?: string | null | undefined;
}

/** Result of {@link getUserDataForResults}: the input enriched with resolved user fields. */
export type WithUserData<T> = T & {
  displayName?: string;
  creatorName?: string;
  emailAddress?: string;
};

export const getUserDataForResults = async <T extends IData>(
  httpClient: AxiosInstance,
  userIds: string[],
  results: T[],
  getUserIdInObject: (comment: T) => string,
): Promise<WithUserData<T>[]> => {
  const distinctUserIds = userIds
    .filter((el) => !!el)
    .filter((userId, index) => userIds.indexOf(userId) === index);

  if (distinctUserIds.length === 0) {
    return results as WithUserData<T>[];
  }

  const accountUsers = await fetchUsersByIds<TempoUserResponse>(httpClient, distinctUserIds);

  const resultsWithUserData = results.map((item) => {
    let name: string | undefined = item.displayName ?? item.creatorName ?? undefined;
    let email: string | undefined = item.emailAddress ?? item.email ?? undefined;
    const userId = getUserIdInObject(item);
    if (userId && userId.length > 0) {
      name = 'Unknown';
      const userFound = accountUsers.find((user) => user.id === userId);
      if (userFound) {
        name = userFound.name;
        email = userFound.email;
      }
    }
    return {
      ...item,
      displayName: name,
      creatorName: name,
      emailAddress: email,
    } as WithUserData<T>;
  });

  return resultsWithUserData;
};

export const useFim3Api = () => {
  const httpClient = axios.create({
    baseURL: `${getEnv().fimApiLocalBaseUrl ?? getEnv().apiBaseUrl}/api/v1/fim`,
  });

  httpClient.interceptors.request.use(async (config) => {
    try {
      const token = await fetchAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (_error) {
      // Failed to get access token - request will proceed without authentication
    }
    return config;
  });

  // TODO: Use API client instead of low-level http/s3 client(s)
  const productAreasApi = useProductAreasApi(httpClient);
  const feedbackFormsApi = useFeedbackFormsApi(httpClient);
  const ideasApi = useIdeasApi(httpClient);
  const ideaViewsApi = useIdeaViewsApi(httpClient);
  const demandsApi = useDemandsApi(httpClient);
  const demandReviewersApi = useDemandReviewersApi(httpClient);
  const demandChecklistApi = useDemandChecklistApi(httpClient);
  const demandViewsApi = useDemandViewsApi(httpClient);
  const demandStatusesApi = useDemandStatusesApi(httpClient);
  const demandTypesApi = useDemandTypesApi(httpClient);
  const customFieldsApi = useCustomFieldsApi(httpClient);
  const feedbackApi = useFeedbackApi(httpClient);
  const votesApi = useVotesApi(httpClient);
  const ranksApi = useRanksApi(httpClient);
  const s3Api = useS3Api(httpClient);
  const activityLogsApi = useActivityLogsApi(httpClient);
  const customersApi = useCustomersApi(httpClient);
  const accountSettingsApi = useAccountSettingsApi(httpClient);
  const bulkImportApi = useBulkImportApi(httpClient);
  const portalsApi = usePortalsApi(httpClient);
  const priorityFrameworkApi = usePriorityFrameworkApi(httpClient);
  const notificationSubscriptionsApi = useNotificationSubscriptions(httpClient);
  const completionEventsApi = useCompletionEventsApi(httpClient);
  const completionNotificationsApi = useCompletionNotificationsApi(httpClient);
  const userInteractionsApi = useUserInteractionsApi(httpClient);

  return {
    productAreasApi,
    feedbackFormsApi,
    ideasApi,
    ideaViewsApi,
    demandsApi,
    demandReviewersApi,
    demandChecklistApi,
    demandViewsApi,
    demandStatusesApi,
    demandTypesApi,
    votesApi,
    ranksApi,
    customFieldsApi,
    feedbackApi,
    s3Api,
    activityLogsApi,
    customersApi,
    accountSettingsApi,
    bulkImportApi,
    portalsApi,
    httpClient,
    priorityFrameworkApi,
    notificationSubscriptionsApi,
    completionEventsApi,
    completionNotificationsApi,
    userInteractionsApi,
  };
};
